/* eslint-disable max-len */
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useNotify, useRefresh, useTranslate, useUpdate } from "react-admin";
import { muiColors } from "themes/colors";
import { CustomButton } from "./CustomButton";
import { Dialog, DialogTitle, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import * as Axios from "providers/axios";
import { AxiosResponse } from "axios";
import * as User from "providers/user";
import { LoadingIcon } from "assets/Icons";

interface CancellationModalProps {
  cancelModalAppointmentId: string | null;
  setCancelModalAppointmentId: Dispatch<SetStateAction<string | null>>;
  refreshCalendarAndCloseShowModal?: () => void;
}

interface Response {
  cancellation_type:
    | "lateFeeCancellation"
    | "noShowFeeCancellation"
    | "creditsGivenCancellation"
    | "refundGivenCancellation"
    | "noChargeNoCreditsCancellation";
  cancellation_result: CancellationResult;
  policy: Policy;
  user_pays_fees: boolean;
}

interface CancellationResult {
  cancellation_window: "late" | "no-show" | "none";
  cancellation_fee: number;
  total_fee?: number;
  total_credits?: number;
  total_refund?: number;
  potential_stripe_refund_amount?: number;
  amount_captured: number;
  total_fee_with_stripe_fees?: number;
  is_refundable: boolean;
}

interface Policy {
  late_cancellation_hours: number;
  late_cancellation_charge: number;
  no_show_charge: number;
}

type PendingCancellationType = "STANDARD" | "ADMIN_NOFEES" | null;
type StripeRefundCancellationType =
  | "STRIPE_REFUND"
  | "STRIPE_REFUND_ADMIN_NOFEES";

export default function CancellationModal({
  cancelModalAppointmentId,
  setCancelModalAppointmentId,
  refreshCalendarAndCloseShowModal
}: CancellationModalProps) {
  const { api } = Axios.useAxios();
  const { permissions } = User.useUser();
  const translate = useTranslate();
  const [cancellationData, setCancellationData] = useState<Response | null>(
    null
  );
  const [pendingCancellationType, setPendingCancellationType] =
    useState<PendingCancellationType>(null);
  const [stripeRefundPreviewAmount, setStripeRefundPreviewAmount] = useState<
    number | null
  >(null);
  const [isStripeRefundPreviewLoading, setIsStripeRefundPreviewLoading] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [edit] = useUpdate();
  const refresh = useRefresh();
  const notify = useNotify();
  const isCompanyLimited = permissions?.isCompanyLimited;
  const isRefundable = cancellationData?.cancellation_result?.is_refundable;

  const handleCancellation = (cancellationType: string) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const updatedAppointment = {
      id: cancelModalAppointmentId,
      data: {
        cancelled_at: new Date().toISOString(),
        cancelled_type: cancellationType
      }
    };

    edit("Appointment", updatedAppointment, {
      onSuccess: () => {
        refresh();
        setCancelModalAppointmentId(null);
        if (refreshCalendarAndCloseShowModal) {
          refreshCalendarAndCloseShowModal();
        }
      },
      onError: () => {
        setIsSubmitting(false);
        notify("Error occurred during Cancellation, please try again later", {
          type: "error",
          undoable: false,
          anchorOrigin: { vertical: "bottom", horizontal: "center" }
        });
      }
    });
  };

  const getCancellationData = async () => {
    const res: AxiosResponse<Response> = await api.post(
      `${process.env.REACT_APP_API_CORE_URL}/v1/cancellation-policy`,
      {
        appointment_id: cancelModalAppointmentId
      }
    );
    setCancellationData(res.data);
  };

  const getStripeRefundPreviewAmount = async (
    cancellationType: StripeRefundCancellationType
  ) => {
    setStripeRefundPreviewAmount(null);
    setIsStripeRefundPreviewLoading(true);

    try {
      const res: AxiosResponse<Response> = await api.post(
        `${process.env.REACT_APP_API_CORE_URL}/v1/cancellation-policy`,
        {
          appointment_id: cancelModalAppointmentId,
          cancelled_type: cancellationType
        }
      );
      const refundAmount =
        res.data.cancellation_result.total_refund ??
        res.data.cancellation_result.potential_stripe_refund_amount ??
        0;

      setStripeRefundPreviewAmount(refundAmount);
    } catch (e) {
      notify("Error occurred while calculating Stripe refund amount", {
        type: "error",
        undoable: false,
        anchorOrigin: { vertical: "bottom", horizontal: "center" }
      });
    } finally {
      setIsStripeRefundPreviewLoading(false);
    }
  };

  useEffect(() => {
    getCancellationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!cancelModalAppointmentId) {
      setPendingCancellationType(null);
      setStripeRefundPreviewAmount(null);
      setIsStripeRefundPreviewLoading(false);
      setIsSubmitting(false);
    }
  }, [cancelModalAppointmentId]);

  const result = cancellationData?.cancellation_result;
  const isPaidByCustomer =
    result?.amount_captured !== undefined && result.amount_captured > 0;
  const isLateOrNoShowWindow =
    result?.cancellation_window === "late" ||
    result?.cancellation_window === "no-show";
  const isLateOrNoShowType =
    cancellationData?.cancellation_type === "lateFeeCancellation" ||
    cancellationData?.cancellation_type === "noShowFeeCancellation";

  const willRefundOnStandard =
    !isCompanyLimited && isPaidByCustomer && !isLateOrNoShowType;
  const willRefundOnWaive =
    !isCompanyLimited && isLateOrNoShowWindow && isPaidByCustomer;

  const handleStandardClick = () => {
    if (willRefundOnStandard) {
      setPendingCancellationType("STANDARD");
      void getStripeRefundPreviewAmount("STRIPE_REFUND");
    } else {
      handleCancellation("STANDARD");
    }
  };

  const handleWaiveClick = () => {
    if (willRefundOnWaive) {
      setPendingCancellationType("ADMIN_NOFEES");
      void getStripeRefundPreviewAmount("STRIPE_REFUND_ADMIN_NOFEES");
    } else {
      handleCancellation("ADMIN_NOFEES");
    }
  };

  const handleStripeRefundClick = () => {
    if (
      isStripeRefundPreviewLoading ||
      !stripeRefundPreviewAmount ||
      stripeRefundPreviewAmount <= 0
    ) {
      return;
    }

    handleCancellation(
      pendingCancellationType === "ADMIN_NOFEES"
        ? "STRIPE_REFUND_ADMIN_NOFEES"
        : "STRIPE_REFUND"
    );
  };

  const stepTwoStoreCreditAmount =
    pendingCancellationType === "STANDARD" &&
    cancellationData?.cancellation_type === "creditsGivenCancellation" &&
    result?.total_credits
      ? result.total_credits
      : result?.amount_captured || 0;
  const stepTwoStripeRefundAmount = stripeRefundPreviewAmount || 0;
  const displayedStripeRefundAmount = isStripeRefundPreviewLoading
    ? "..."
    : stepTwoStripeRefundAmount.toFixed(2);
  const canRefundToStripe =
    !isStripeRefundPreviewLoading && stepTwoStripeRefundAmount > 0;

  const isStepTwo = pendingCancellationType !== null;

  const cancellationWindowLabel =
    result?.cancellation_window === "no-show"
      ? translate("cancellation_modal.no_show_window")
      : translate("cancellation_modal.late_window");

  if (!cancellationData) {
    return (
      <Dialog
        open={Boolean(cancelModalAppointmentId)}
        onClose={() => setCancelModalAppointmentId(null)}
      >
        <DialogTitle>
          <div className="text-[20px] px-6 text-center">
            {translate("common.cancellation_confirmation")}
          </div>
          <IconButton
            sx={{
              position: "absolute",
              top: "8px",
              right: "8px"
            }}
            onClick={() => setCancelModalAppointmentId(null)}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <div className="w-full h-[120px] flex justify-center items-center">
          <div className="loadingSpinAnimation">
            <LoadingIcon />
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={Boolean(cancelModalAppointmentId)}
      onClose={() => setCancelModalAppointmentId(null)}
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: muiColors.paper,
          borderRadius: "8px"
        },
        "& #edit-dialog-title": {
          textAlign: "center",
          textTransform: "uppercase",
          fontFamily: "Inter",
          fontSize: "14px"
        },
        "& .RaFormDialogTitle-closeButton": {
          display: "none"
        }
      }}
    >
      <DialogTitle>
        <div className="text-[20px] px-6 text-center">
          {translate("common.cancellation_confirmation")}
        </div>
        <IconButton
          sx={{
            position: "absolute",
            top: "8px",
            right: "8px"
          }}
          onClick={() => setCancelModalAppointmentId(null)}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <div>
        <div className="p-4">
          {!isStepTwo && (
            <>
              {/* {!isCompanyLimited && isPaidByCustomer && (
                <span>
                  {translate("cancellation_modal.customer_already_paid", {
                    amount: result?.amount_captured
                  })}
                </span>
              )} */}
              {/* {!isCompanyLimited &&
                isLateOrNoShowWindow &&
                !isPaidByCustomer && (
                  <span>
                    {" "}
                    {translate("cancellation_modal.customer_not_paid_yet")}
                  </span>
                )} */}

              {/* {!isCompanyLimited &&
                isLateOrNoShowType &&
                result?.cancellation_fee &&
                result?.total_fee_with_stripe_fees && (
                  <span>
                    {" "}
                    {translate("cancellation_modal.fee_will_be_charged", {
                      window: cancellationWindowLabel,
                      fee: result.cancellation_fee,
                      total: result.total_fee_with_stripe_fees
                    })}
                  </span>
                )} */}
              {!isCompanyLimited &&
                isLateOrNoShowType &&
                result?.cancellation_fee &&
                result?.total_fee_with_stripe_fees && (
                  <span>
                    {" "}
                    This appointment falls within the salon's{" "}
                    {cancellationWindowLabel} cancellation window. A fee of $
                    {result.cancellation_fee} is required
                    {cancellationData?.user_pays_fees
                      ? " (Cancel Fee + Processing Fee)."
                      : "."}
                  </span>
                )}
              {/* {!isCompanyLimited &&
                isLateOrNoShowWindow &&
                cancellationData?.cancellation_type ===
                  "creditsGivenCancellation" &&
                result?.cancellation_fee &&
                result?.total_credits && (
                  <span>
                    {" "}
                    {translate("cancellation_modal.fee_will_be_credited_back", {
                      window: cancellationWindowLabel,
                      fee: result.cancellation_fee,
                      credits: result.total_credits
                    })}
                  </span>
                )} */}
              {!isCompanyLimited &&
                isLateOrNoShowWindow &&
                cancellationData?.cancellation_type ===
                  "creditsGivenCancellation" &&
                result?.cancellation_fee &&
                result?.total_credits && (
                  <span>
                    {" "}
                    The customer prepaid ${result?.amount_captured} for this
                    appointment and owes a ${result?.cancellation_fee}{" "}
                    {cancellationWindowLabel} cancellation fee. If the{" "}
                    {cancellationWindowLabel} cancellation fee is applied, the
                    remaining ${result?.total_credits} will be refunded to the
                    guest. Choose how you would like to process this
                    cancellation?
                  </span>
                )}
              {!isCompanyLimited &&
                result?.cancellation_window === "none" &&
                !isPaidByCustomer && (
                  <span>
                    {" "}
                    {translate("cancellation_modal.are_you_sure_to_cancel")}
                  </span>
                )}
              {!isCompanyLimited &&
                result?.cancellation_window === "none" &&
                isPaidByCustomer && (
                  <span>
                    {" "}
                    This appointment was prepaid. In the next step, you can
                    choose to refund the customer or give store credits. Are you
                    sure you want to cancel this appointment?
                  </span>
                )}
              {isCompanyLimited && isRefundable && (
                <span>
                  {translate(
                    "cancellation_modal.are_you_sure_to_cancel_appointment"
                  )}{" "}
                </span>
              )}
              {isCompanyLimited && !isRefundable && (
                <span>
                  {translate("cancellation_modal.late_cancelation_warning")}
                </span>
              )}
              <div
                className={
                  !isCompanyLimited && isLateOrNoShowWindow
                    ? "flex flex-col mt-6 items-center gap-2"
                    : "flex justify-between flex-row-reverse mt-6"
                }
              >
                <CustomButton
                  onClick={handleStandardClick}
                  cyId="cancel-appointment-confirm-button"
                  text={
                    !isCompanyLimited &&
                    isLateOrNoShowWindow &&
                    cancellationData?.cancellation_type ===
                      "creditsGivenCancellation" &&
                    result?.cancellation_fee &&
                    result?.total_credits
                      ? `Cancel and Apply Fee (Refund $${result?.total_credits})`
                      : !isCompanyLimited &&
                        isLateOrNoShowWindow &&
                        !isPaidByCustomer
                      ? "Cancel and Charge Fee"
                      : translate("common.yes_cancel")
                  }
                  disabled={isSubmitting}
                  sx={{
                    backgroundColor: muiColors.error,
                    "&:hover": {
                      backgroundColor: muiColors.error,
                      opacity: 0.9
                    }
                  }}
                />
                {!isCompanyLimited &&
                  isLateOrNoShowWindow &&
                  isPaidByCustomer && (
                    <CustomButton
                      onClick={handleWaiveClick}
                      text={
                        !isCompanyLimited &&
                        isLateOrNoShowWindow &&
                        cancellationData?.cancellation_type ===
                          "creditsGivenCancellation" &&
                        result?.cancellation_fee &&
                        result?.total_credits
                          ? `Cancel and Waive Fee (Refund $${result?.amount_captured})`
                          : translate(
                              "cancellation_modal.yes_waive_fees_refund",
                              { amount: result?.amount_captured }
                            )
                      }
                      cyId="cancel-appointment-confirm-no-fee-button"
                      disabled={isSubmitting}
                      sx={{
                        minHeight: "36px",
                        height: "unset",
                        py: "10px",
                        whiteSpace: "normal"
                      }}
                    />
                  )}
                {!isCompanyLimited &&
                  isLateOrNoShowWindow &&
                  !isPaidByCustomer && (
                    <CustomButton
                      onClick={handleWaiveClick}
                      text={"Cancel but Don't Charge Fee"}
                      disabled={isSubmitting}
                    />
                  )}
                <CustomButton
                  onClick={() => setCancelModalAppointmentId(null)}
                  text={translate("common.no_go_back")}
                  variant="secondary"
                />
              </div>
            </>
          )}

          {isStepTwo && (
            <>
              {pendingCancellationType === "ADMIN_NOFEES" && (
                <span>
                  {/* {translate("cancellation_modal.fees_waived_refund_message", {
                    amount: displayedStripeRefundAmount
                  })} */}
                  The fees will be waived and the customer will be refunded the
                  full amount. How would you like to process the refund? Store
                  credits are added instantly,and refunds to the original
                  payment method take 5-10 business days.
                </span>
              )}
              {pendingCancellationType === "STANDARD" &&
                cancellationData?.cancellation_type ===
                  "creditsGivenCancellation" &&
                !!result?.cancellation_fee && (
                  <span>
                    {/* {translate(
                      "cancellation_modal.fee_applied_refund_message",
                      {
                        window: cancellationWindowLabel,
                        fee: result.cancellation_fee,
                        amount: displayedStripeRefundAmount
                      }
                    )} */}
                    After applying a late cancellation fee, the remaining value
                    will be refunded to the customer. How would you like to
                    process the refund? Store credits are added instantly, and
                    refunds to the original payment method take 5-10 business
                    days.
                  </span>
                )}
              {!isCompanyLimited &&
                result?.cancellation_window === "none" &&
                isPaidByCustomer && (
                  <span>
                    How would you like to refund the customer for this
                    cancellation?
                    <br />
                    <br />
                    Store credits are added instantly to their account. Refunds
                    to the original payment method may take 5-10 business days.
                  </span>
                )}
              {/* <span>
                {" "}
                {translate("cancellation_modal.how_to_refund_explanation")}
              </span> */}
              <div className="flex flex-col mt-6 items-center gap-2">
                <CustomButton
                  onClick={handleStripeRefundClick}
                  text={translate(
                    "cancellation_modal.refund_to_original_button",
                    { amount: displayedStripeRefundAmount }
                  )}
                  cyId="cancel-appointment-real-refund-button"
                  disabled={!canRefundToStripe || isSubmitting}
                  sx={{
                    backgroundColor: muiColors.error,
                    "&:hover": {
                      backgroundColor: muiColors.error,
                      opacity: 0.9
                    },
                    minHeight: "36px",
                    height: "unset",
                    py: "10px",
                    whiteSpace: "normal"
                  }}
                />
                <CustomButton
                  onClick={() =>
                    pendingCancellationType &&
                    handleCancellation(pendingCancellationType)
                  }
                  text={translate(
                    "cancellation_modal.give_store_credits_button",
                    { amount: stepTwoStoreCreditAmount }
                  )}
                  cyId="cancel-appointment-store-credits-button"
                  disabled={isSubmitting}
                  sx={{
                    minHeight: "36px",
                    height: "unset",
                    py: "10px",
                    whiteSpace: "normal"
                  }}
                />
                <CustomButton
                  onClick={() => {
                    setPendingCancellationType(null);
                    setStripeRefundPreviewAmount(null);
                  }}
                  text={translate("common.no_go_back")}
                  variant="secondary"
                />
              </div>
            </>
          )}
        </div>

        <IconButton
          sx={{
            position: "absolute",
            top: "8px",
            right: "8px"
          }}
          onClick={() => setCancelModalAppointmentId(null)}
        >
          <CloseIcon />
        </IconButton>
      </div>
    </Dialog>
  );
}
